import React, { useMemo } from "react"
import EmployeeOnlyModalContainer from "../../../employee-only-containers/EmployeeOnlyModalContainer"
import { DbgPaths } from "#/utils/Paths"
import { getMedicationCategorySections } from "./multiple-categories/MedicationsCategory"
import { getQmeCategorySections } from "./multiple-categories/QmeCategory"
import useBenefit from "../../../dynamic-content/hooks/useGetBenefit"
import { FinancialSupportErrorMessage } from "./FinancialSupportErrorMessage"
import { useSelector } from "react-redux"
import useCompanyComplianceConfigLineItemFilter from "../hooks/useGetCompanyComplianceConfigLineItemFilter"
import {
  Box,
  CloseIcon,
  Dialog,
  DialogTitle,
  IconButton,
  Link,
  Progress,
  Typography,
  UnorderedList,
  UnorderedListItem
} from "@carrotfertility/carotene-core"
import { FormattedMessage, useIntl } from "react-intl"
import { getCategoryName } from "../ReimbursementCategoryMessages"
import { getCategoryCoverage } from "#/utils/CategoryCoverage"
import { getSubsidies } from "#/redux/reducers/subsidies"
import { getPreservationSections } from "./multiple-categories/PreservationCategory"
import { getFertilityAndPreservationSections } from "./multiple-categories/FertilityAndPreservationCategory"
import { getFertilitySections } from "./multiple-categories/FertilityCategory"
import { getOtherExpensesSections } from "./multiple-categories/OtherExpensesCategory"
import { getMenopauseLowTestosteroneSections } from "./multiple-categories/MenopauseLowTestosteroneCategory"
import { getAdoptionAndGCSections } from "./multiple-categories/AdoptionAndGCCategory"
import { reimbursementCategoryNameMessageKeys } from "../reimbursementCategoryNameMessageKeys"
import { FormattedSubsidy } from "#/components/views/reimbursements/digital-benefit-guide/PayingForCare"
import { useHistory } from "react-router"

// @ts-expect-error TS(7006) FIXME: Parameter 'props' implicitly has an 'any' type.
const PayingForCareContentSub = (props) => {
  const { category, benefitInfo, lineItemFilter, hasQmeCategory, hasMedicationsCategory } = props
  const isOtherExpenses = useMemo(() => category === reimbursementCategoryNameMessageKeys.OTHER, [category])
  const preservation = benefitInfo.journeys.fertilityPreservation
  const fertilityCare = benefitInfo.journeys.fertilityCare
  const hasElectiveFertilityLineItemFilter = lineItemFilter === "ELECTIVE_FERTILITY"
  const showQmeInfertilityMessage =
    fertilityCare.eligibleExpensesDetail.isCoveredWithInfertilityDiagnosis ||
    preservation.eligibleExpensesDetail.isMedicallyNecessaryPreservationCovered
  const showOtherExpensesInfertilityMessage =
    fertilityCare.eligibleExpensesDetail.isElectiveCareCovered ||
    preservation.eligibleExpensesDetail.isElectivePreservationCovered

  return (
    <>
      {!hasElectiveFertilityLineItemFilter &&
        hasQmeCategory &&
        !hasMedicationsCategory &&
        (showQmeInfertilityMessage || showOtherExpensesInfertilityMessage) && (
          <Box>
            <Typography variant="h6">
              <FormattedMessage defaultMessage="How do I know if I have an infertility diagnosis?" />
            </Typography>
            <Box
              sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.xs, display: "flex", flexDirection: "column" })}
            >
              <Typography>
                <FormattedMessage
                  defaultMessage={
                    "If you’re not sure, ask your fertility doctor. Based on local standards" +
                    " of practice, a doctor may diagnose infertility if pregnancy does not occur within a" +
                    " specific timeframe of unprotected intercourse or insemination."
                  }
                />
              </Typography>
            </Box>
            <Box sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.xs })}>
              <Typography>
                <FormattedMessage defaultMessage="An infertility diagnosis may also be issued due to:" />
              </Typography>
            </Box>
            <Box sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.xs })}>
              <UnorderedList>
                <UnorderedListItem>
                  <Typography>
                    <FormattedMessage defaultMessage="Ovulation issues or diminished ovarian reserve" />
                  </Typography>
                </UnorderedListItem>
                <UnorderedListItem>
                  <Typography>
                    <FormattedMessage defaultMessage="Problems with semen, sperm, or ejaculation" />
                  </Typography>
                </UnorderedListItem>
                <UnorderedListItem>
                  <Typography>
                    <FormattedMessage defaultMessage="Anatomical, endocrine, genetic, or functional differences in the reproductive system" />
                  </Typography>
                </UnorderedListItem>
              </UnorderedList>
            </Box>
          </Box>
        )}
      <Box sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.md })}>
        {!isOtherExpenses &&
          !hasElectiveFertilityLineItemFilter &&
          preservation.eligibleExpensesDetail.isMedicallyNecessaryPreservationCovered && (
            <Box>
              <Typography variant="h6">
                <FormattedMessage defaultMessage="How do I know if my preservation care is medically necessary?" />
              </Typography>
              <Box
                sx={(theme) => ({
                  marginBlockStart: theme.tokens.spacing.xs,
                  display: "flex",
                  flexDirection: "column"
                })}
              >
                <Typography>
                  <FormattedMessage
                    defaultMessage={
                      "If you’re not sure, ask your fertility doctor. Medical necessity" +
                      " for fertility preservation is determined by a doctor based on health issues or treatments that" +
                      " could significantly impact the reproductive system. For example, preservation may be considered " +
                      "medically necessary before undergoing certain types of chemotherapy or surgery."
                    }
                  />
                </Typography>
              </Box>
            </Box>
          )}
        {isOtherExpenses &&
          !hasElectiveFertilityLineItemFilter &&
          hasQmeCategory &&
          !hasMedicationsCategory &&
          preservation.eligibleExpensesDetail.isElectivePreservationCovered && (
            <Box sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.md })}>
              <Typography variant="h6">
                <FormattedMessage defaultMessage="How do I know if my care is medically necessary?" />
              </Typography>
              <Box sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.xs })}>
                <Typography>
                  <FormattedMessage
                    defaultMessage={
                      "If you’re not sure, ask your fertility doctor. Medical necessity" +
                      " for fertility preservation is determined by a doctor based on health issues or treatments that" +
                      " could significantly impact the reproductive system. For example, preservation may be considered " +
                      "medically necessary before undergoing certain types of chemotherapy or surgery."
                    }
                  />
                </Typography>
              </Box>
            </Box>
          )}
      </Box>
    </>
  )
}

// @ts-expect-error TS(7006) FIXME: Parameter 'props' implicitly has an 'any' type.
const PayingForCareContentMain = (props): JSX.Element => {
  const { category, hasQmeCategory, hasMedicationsCategory } = props
  const { data: benefit, isLoading } = useBenefit()
  const { data } = useCompanyComplianceConfigLineItemFilter()
  const coverage = getCategoryCoverage(useSelector(getSubsidies))
  const intl = useIntl()

  const sections = getCategorySections(category, hasQmeCategory)

  if ((data == null || benefit == null) && !isLoading) {
    return <FinancialSupportErrorMessage />
  }

  return isLoading ? (
    <Box display="flex" justifyContent="center" alignItems="center">
      <Progress />
    </Box>
  ) : (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Box sx={(theme) => ({ padding: "none", marginBlockEnd: theme.tokens.spacing.xl })}>
        <DialogTitle id="fund-category-name">{intl.formatMessage(getCategoryName(category, coverage.qme))}</DialogTitle>
        <Box sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.md })}>
          <Box>
            <Typography>{sections.header}</Typography>
            <Box sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.xs })}>
              <UnorderedList>{sections.listItems}</UnorderedList>
              {sections.footer && (
                <Box sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.xs })}>
                  <Typography>{sections.footer}</Typography>
                </Box>
              )}
            </Box>
          </Box>
          {sections.includeSub && (
            <Box sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.md })}>
              <PayingForCareContentSub
                category={category}
                benefitInfo={benefit}
                lineItemFilter={data}
                hasQmeCategory={hasQmeCategory}
                hasMedicationsCategory={hasMedicationsCategory}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export function PayingForCareCoverageModal({
  subsidyCategory,
  hasQmeCategory,
  hasMedicationsCategory
}: {
  subsidyCategory: FormattedSubsidy
  hasQmeCategory: boolean
  hasMedicationsCategory: boolean
}): JSX.Element {
  const [showModal, setShowModal] = React.useState(false)
  const intl = useIntl()

  return (
    <>
      <CategoryLearnLink
        category={subsidyCategory.reimbursementCategoryNameMessageKey}
        handleOpen={() => setShowModal(true)}
      />
      <EmployeeOnlyModalContainer onClose={() => setShowModal(false)}>
        <Dialog
          aria-labelledby="fund-category-name"
          onClose={(event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation()
            setShowModal(false)
          }}
          open={showModal}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end"
            }}
          >
            <IconButton
              aria-label={intl.formatMessage({ defaultMessage: "close" })}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation()
                setShowModal(false)
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <PayingForCareContentMain
            category={subsidyCategory.reimbursementCategoryNameMessageKey}
            hasQmeCategory={hasQmeCategory}
            hasMedicationsCategory={hasMedicationsCategory}
          />
          <Box sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.xxxl })}></Box>
        </Dialog>
      </EmployeeOnlyModalContainer>
    </>
  )
}

function CategoryLearnLink({ category, handleOpen }: { category: string; handleOpen: () => void }): React.JSX.Element {
  const { data: benefit } = useBenefit()
  const history = useHistory()

  let href: string = null
  switch (category) {
    case reimbursementCategoryNameMessageKeys.ADOPTION: {
      href = DbgPaths.ADOPTION_COVERAGE
      break
    }
    case reimbursementCategoryNameMessageKeys.GC: {
      href = DbgPaths.GESTATIONAL_SURROGACY_COVERAGE
      break
    }
    case reimbursementCategoryNameMessageKeys.FERTILITY: {
      if (
        !benefit?.journeys.fertilityCare.eligibleExpensesDetail.isElectiveCareCovered &&
        !benefit?.journeys.fertilityCare.eligibleExpensesDetail.isCoveredWithInfertilityDiagnosis
      ) {
        href = DbgPaths.FERTILITY_COVERAGE
      }
      break
    }
    case reimbursementCategoryNameMessageKeys.GAC: {
      href = DbgPaths.GENDER_AFFIRMING_CARE
      break
    }
    case reimbursementCategoryNameMessageKeys.PREGNANCY: {
      href = DbgPaths.PREGNANCY_COVERAGE
      break
    }
    case reimbursementCategoryNameMessageKeys.PRESERVATION: {
      if (
        benefit?.journeys.fertilityPreservation.eligibleExpensesDetail.isElectivePreservationCovered ||
        benefit?.journeys.fertilityPreservation.eligibleExpensesDetail.isMedicallyNecessaryPreservationCovered ||
        benefit?.journeys.fertilityCare.eligibleExpensesDetail.hasDonorAssistance
      ) {
        href = DbgPaths.PRESERVATION_COVERAGE
      }
      break
    }
  }

  return (
    <Typography
      variant="body1"
      color={(theme) => theme.palette.text.secondary}
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: (theme) => theme.spacing(theme.tokens.spacing.xxs)
      }}
    >
      <FormattedMessage
        defaultMessage="<link>Learn</link> what this includes"
        values={{
          link: (linkContent) => (
            <Link
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation()
                if (href) {
                  history.push(href)
                }
                handleOpen()
              }}
              color="inherit"
              component="button"
              sx={{
                pointerEvents: "auto"
              }}
            >
              {linkContent}
            </Link>
          )
        }}
      />
    </Typography>
  )
}

export interface CategorySections {
  header: React.JSX.Element
  listItems?: React.JSX.Element
  footer?: React.JSX.Element
  includeSub?: boolean
}

export function getCategorySections(category: string, hasQmeCategory: boolean): CategorySections {
  switch (category) {
    case reimbursementCategoryNameMessageKeys.FERTILITY_GC_ADOPTION:
    case reimbursementCategoryNameMessageKeys.ADOPTION_AND_GC: {
      return getAdoptionAndGCSections(category)
    }
    case reimbursementCategoryNameMessageKeys.MEDICATION: {
      return getMedicationCategorySections()
    }
    case reimbursementCategoryNameMessageKeys.QME: {
      return getQmeCategorySections()
    }
    case reimbursementCategoryNameMessageKeys.M_LT:
    case reimbursementCategoryNameMessageKeys.M_LT_GAC:
    case reimbursementCategoryNameMessageKeys.PREGNANCY_M_LT:
    case reimbursementCategoryNameMessageKeys.FERTILITY_M_LT: {
      return getMenopauseLowTestosteroneSections(category)
    }
    case reimbursementCategoryNameMessageKeys.FERTILITY: {
      return getFertilitySections()
    }
    case reimbursementCategoryNameMessageKeys.FERTILITY_GC_ADOPTION:
    case reimbursementCategoryNameMessageKeys.FERTILITY_AND_PRESERVATION: {
      return getFertilityAndPreservationSections()
    }
    case reimbursementCategoryNameMessageKeys.PRESERVATION: {
      return getPreservationSections()
    }
  }
  return getOtherExpensesSections(hasQmeCategory)
}
